import { Maybe, TicketLocation } from "./avroGraphql";
import Big from "big.js";
import dayjs from "dayjs";
import { FUEL_LINE_ITEM_TYPES, LOCATION_MRU_DAYS_BACK_KEY, LOCATION_MRU_SHOW_KEY, MINIMUM_LINE_ITEM_TYPES, SELECTED_TICKET_ID_KEY, TIME_ENTRY_STYLE_KEY } from "./metadata";
import { DEMO_MODE_KEY } from "../Utils/TicketDatabase";
import { TicketExtended } from "../Utils/TypeExtensions";
import { createInitializationTicket, isInitializationTicket } from "../Utils/UtilityMethods";
import { TIME_ENTRY_STYLES } from "../routes/flightPackage/userPreferences";

export function DefinitelyAnArray<T>(nullableArray: Maybe<Array<Maybe<T>>> | undefined): Array<T> {
    if (!nullableArray || !Array.isArray(nullableArray)) return [] as T[];
    return nullableArray as T[];
}

export function BigOrZeroIfInvalid(possibleNumber: Maybe<number> | string | undefined): Big {
    let result = new Big(0);
    try {
        result = new Big(possibleNumber ?? 0);
    } catch {
        result = new Big(0);
    } finally {
        // fricking eslint
    }

    return result;
}

export function HasValue(target: string | Maybe<string> | Maybe<number> | Maybe<boolean> | null | undefined): boolean {
    return target !== "" && target !== null && target !== undefined;
}

export function GetLineItemPrecision(lineItemTypeId: number): number {
    if (
        MINIMUM_LINE_ITEM_TYPES.find((t) => t === lineItemTypeId) ||
        FUEL_LINE_ITEM_TYPES.find((t) => t === lineItemTypeId) ||
        MINIMUM_LINE_ITEM_TYPES.find((t) => t === lineItemTypeId)
    ) {
        return 1;
    }

    return 0;
}

export function sortFlightLegsManually(a: TicketLocation, b: TicketLocation): number {
    // != null checks for both null and undefined
    return (a.sort_order != null ? a.sort_order : a.id) - (b.sort_order != null ? b.sort_order : b.id);
}

export function sortFlightLegsByTime(a: TicketLocation, b: TicketLocation): number {
    if (dayjs(a.up_time).isBefore(dayjs(b.up_time))) return -1;
    if (dayjs(a.up_time).isAfter(dayjs(b.up_time))) return 1;
    return 0;
}

export function IsDemoMode(): boolean {
    return localStorage.getItem(DEMO_MODE_KEY) === "Y";
}

export function ShowSuggestions(): boolean {
    return (localStorage.getItem(LOCATION_MRU_SHOW_KEY) || "Y") === "Y";
}

export function MruDaysBack(): number {
    return Number(localStorage.getItem(LOCATION_MRU_DAYS_BACK_KEY) || 999);
}

export function bookmarkTicket(ticket: TicketExtended): void {
    localStorage.setItem(TICKET_AND_MODE_KEY(), getTicketNumber(ticket));
}

export function getBookmarkedTicketId(sortedTicketList: TicketExtended[]): number {
    const bookmarkedNumber = localStorage.getItem(TICKET_AND_MODE_KEY());

    let bookmarkedTicket: TicketExtended | undefined = createInitializationTicket();

    if (HasValue(bookmarkedNumber) && sortedTicketList && sortedTicketList.length > 0) {
        bookmarkedTicket = sortedTicketList.find((t) => getTicketNumber(t) === bookmarkedNumber) || createInitializationTicket();
    }

    if (isInitializationTicket(bookmarkedTicket) && sortedTicketList?.length > 0) {
        bookmarkedTicket = sortedTicketList[0];
    }

    return bookmarkedTicket.id;
}

export const TICKET_AND_MODE_KEY = (): string => {
    return SELECTED_TICKET_ID_KEY + (IsDemoMode() ? "_demo" : "_live");
};

export function getTicketNumber(ticket: TicketExtended): string {
    const revisionText = getRevisionText(ticket);
    return `${ticket.ticket_num}${revisionText}`;
}

function getRevisionText(ticket: TicketExtended): string {
    return ticket.revision_num > 0 ? `-RV${ticket.revision_num.toFixed(0)}` : "";
}

export function getLastRevision(ticketList: TicketExtended[], ticket: TicketExtended): TicketExtended | undefined {
    if (!ticketList) return undefined;

    const matchingRevisions = ticketList
        .filter((t) => {
            return t.date === ticket.date && t.job_id === ticket.job_id;
        })
        .sort((a, b) => b.revision_num - a.revision_num);
    return matchingRevisions[0];

    // hmm, what changed to make this no longer work??
    // .at(0);
}

export function isLastRevision(ticketList: TicketExtended[], ticket: TicketExtended): boolean {
    return ticket.id === getLastRevision(ticketList, ticket)?.id;
}

export function GetTimeEntryStyle(): string {
    return localStorage.getItem(TIME_ENTRY_STYLE_KEY) ?? TIME_ENTRY_STYLES[0];
}

export function isValidTimeString(value: string): boolean {
    return HasValue(value) && value.length > 5 && dayjs(value).isValid(); // cheesy but should work, any valid values will be ISO format
}

export function isInvalidTimeString(value: string): boolean {
    return HasValue(value) ? !(value.length > 5 && dayjs(value).isValid()) : false; // ugh, don't want empty value to be invalid
}
